<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, Input, PageHeader, Section, Spinner, Toolbar } from '@drama/ui'
import { useAdminAuth } from '../composables/useAdminAuth.js'
import { getDashboard, getDashboardModelUsage } from '../api/analytics.js'
import DashboardTrend from '../components/DashboardTrend.vue'
import { cumulativeUserSeries } from '../lib/index.js'

const { t, locale } = useI18n()
const auth = useAdminAuth()
const can_view_models = computed(() => auth.account.value?.role === 'admin')
const model_usage = ref(null)
const model_loading = ref(false)
const model_error = ref('')
let model_request_id = 0
const model_series = computed(() => {
  if (!model_usage.value) return { calls: [], tokens: [] }
  const rows = new Map(model_usage.value.daily.map(row => [row.date, row]))
  const series = keys => keys.map(key => ({
    key, label: t(`visualGame.dashboard.models.${key}`),
    values: dates.value.map(date => rows.has(date) ? rows.get(date)[key] : 0),
  }))
  return { calls: series(['calls', 'succeeded', 'failed', 'aborted']), tokens: series(['input_tokens', 'output_tokens']) }
})
async function loadModelUsage(query) {
  const request_id = ++model_request_id
  model_loading.value = true
  model_error.value = ''
  model_usage.value = null
  try {
    const result = await getDashboardModelUsage(query)
    if (request_id === model_request_id) model_usage.value = result
  } catch (cause) {
    if (request_id === model_request_id) model_error.value = cause.message
  } finally {
    if (request_id === model_request_id) model_loading.value = false
  }
}
const filters = reactive({ since: day(-29), until: day(0), device: '' })
const report = ref(null)
const report_device = ref('')
const loading = ref(false)
const error = ref('')
const mode = ref('count')
const groups = [
  { key: 'traffic', metrics: ['visits'] },
  { key: 'auth', metrics: ['registrations', 'logins'] },
  { key: 'usage', metrics: ['interactions', 'text_input', 'voice_input'] },
  { key: 'conversion', metrics: ['paypoints', 'purchase_page', 'checkout', 'orders', 'paid'] },
  { key: 'payments', metrics: ['paid', 'pending', 'failed', 'canceled', 'refunded', 'disputed'] },
  { key: 'credits', metrics: ['daily_claim'] },
]
const summary_metrics = [
  { key: 'visits', measure: 'count' },
  { key: 'visits', measure: 'users' },
  { key: 'registrations', measure: 'count' },
  { key: 'active_users', measure: 'users', label: 'visualGame.dashboard.growth.activeUsers' },
  { key: 'interactions', measure: 'count' },
  { key: 'interactions', measure: 'users' },
  { key: 'paid', measure: 'count' },
  { key: 'paid', measure: 'users' },
]
const summaries = computed(() => {
  if (!report.value) return []
  return ['new', 'cumulative'].map(scope => {
    const rows = summary_metrics.map(({ key, measure, label }) => {
      const metric_label = t(`visualGame.dashboard.metrics.${key}`)
      return {
        key: `${key}_${measure}`,
        label: label ? t(label) : measure === 'users' ? t('visualGame.dashboard.metricUsers', { metric: metric_label }) : metric_label,
        value: number((scope === 'new' ? report.value.metrics[key].total : report.value.cumulative[key])[measure]),
      }
    })
    const currencies = [...new Set([...report.value.revenue.map(item => item.currency), ...report.value.cumulative_revenue.map(item => item.currency)])].sort()
    for (const currency of currencies) {
      const amount_cents = scope === 'new'
        ? (report.value.revenue.find(item => item.currency === currency)?.daily || []).reduce((sum, row) => sum + row.amount_cents, 0)
        : report.value.cumulative_revenue.find(item => item.currency === currency)?.amount_cents || 0
      rows.push({ key: `revenue_${currency}`, label: t('visualGame.dashboard.currencyRevenue', { currency: currency.toUpperCase() }), value: new Intl.NumberFormat(locale.value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount_cents / 100) })
    }
    return { scope, rows }
  })
})
const dates = computed(() => {
  if (!report.value) return []
  const result = []
  for (let time = Date.parse(report.value.since); time < Date.parse(report.value.until); time += 86400000) result.push(new Date(time).toISOString().slice(0, 10))
  return result
})
const growth_series = computed(() => {
  if (!report.value) return []
  return [
    { label: t('visualGame.dashboard.growth.newUsers'), tone: 'info', values: dailyValues('registrations', 'count') },
    { label: t('visualGame.dashboard.growth.activeUsers'), tone: 'success', values: dailyValues('active_users', 'users') },
  ]
})
const cumulative_user_series = computed(() => {
  if (!report.value) return []
  return [{
    label: t('visualGame.dashboard.growth.totalUsers'), tone: 'success', area: true,
    values: cumulativeUserSeries(dates.value, report.value.cumulative.registrations.count, report.value.registration_daily),
  }]
})
function dailyValues(key, measure) {
  const daily = new Map(report.value.metrics[key].daily.map(row => [row.date, row[measure]]))
  return dates.value.map(date => daily.get(date) ?? 0)
}
const order_series_styles = {
  paid: { tone: 'success' },
  pending: { tone: 'info' },
  failed: { tone: 'danger' },
  canceled: { tone: 'textMuted', line_style: 'dashed', symbol: 'rect' },
  refunded: { tone: 'warning' },
  disputed: { tone: 'text', line_style: 'dotted', symbol: 'diamond' },
}
const charts = computed(() => groups.map(group => ({ ...group, series: group.metrics.map(key => {
  const daily = new Map(report.value.metrics[key].daily.map(row => [row.date, row[mode.value]]))
  return { key, ...order_series_styles[key], values: dates.value.map(date => daily.get(date) ?? 0) }
}) })))
const revenue_series = computed(() => (report.value?.revenue || []).map(item => ({
  key: item.currency, label: item.currency,
  values: dates.value.map(date => Number(item.daily.find(row => row.date === date)?.amount_cents || 0) / 100),
})))
function day(offset) {
  const date = new Date()
  date.setUTCDate(date.getUTCDate() + offset)
  return date.toISOString().slice(0, 10)
}
function iso(date) { return new Date(`${date}T00:00:00Z`).toISOString() }
async function load() {
  if (loading.value) return
  error.value = ''
  const since = Date.parse(`${filters.since}T00:00:00Z`)
  const until = Date.parse(`${filters.until}T00:00:00Z`) + 86400000
  if (!Number.isFinite(since) || !Number.isFinite(until) || until <= since || until - since > 366 * 86400000) {
    error.value = t('visualGame.dashboard.invalidRange')
    return
  }
  loading.value = true
  try {
    const query = { since: iso(filters.since), until: new Date(until).toISOString(), device_types: filters.device }
    report.value = await getDashboard(query)
    report_device.value = query.device_types
    if (can_view_models.value) void loadModelUsage(query)
  } catch (cause) {
    error.value = cause.message
  } finally {
    loading.value = false
  }
}
function isPreset(days) {
  return filters.since === day(1 - days) && filters.until === day(0)
}
function preset(days) {
  filters.since = day(1 - days)
  filters.until = day(0)
  load()
}
function number(value) { return new Intl.NumberFormat(locale.value).format(value) }
onMounted(load)
</script>

<template>
  <div class="dashboard vga-page" data-testid="analytics-dashboard">
    <PageHeader :title="t('visualGame.dashboard.title')" />
    <p class="dashboard__note">{{ t('visualGame.dashboard.description') }}</p>
    <Section>
      <Toolbar>
        <div class="drama-toolbar-field"><label for="dashboard-since">{{ t('visualGameAdmin.analytics.since') }}</label><Input id="dashboard-since" v-model="filters.since" type="date" size="sm" /></div>
        <div class="drama-toolbar-field"><label for="dashboard-until">{{ t('visualGameAdmin.analytics.until') }}</label><Input id="dashboard-until" v-model="filters.until" type="date" size="sm" /></div>
        <div class="drama-toolbar-field">
          <label for="dashboard-device">{{ t('visualGameAdmin.conversionFunnelDevices.label') }}</label>
          <select id="dashboard-device" v-model="filters.device">
            <option v-for="device in ['', 'desktop', 'mobile', 'tablet']" :key="device" :value="device">{{ t(`visualGameAdmin.conversionFunnelDevices.${device || 'all'}`) }}</option>
          </select>
        </div>
        <template #actions><Button :loading="loading" data-testid="dashboard-refresh" @click="load">{{ t('visualGameAdmin.analytics.refresh') }}</Button></template>
      </Toolbar>
      <div class="dashboard__presets">
        <Button v-for="days in [1, 7, 30, 90, 365]" :key="days" class="dashboard__preset" :variant="isPreset(days) ? 'primary' : 'secondary'" size="sm" :aria-pressed="isPreset(days)" :data-testid="`dashboard-preset-${days}`" :disabled="loading" @click="preset(days)">{{ t('visualGame.dashboard.days', { days }) }}</Button>
        <RouterLink to="/analytics/conversion-funnel">{{ t('visualGameAdmin.app.conversionFunnel') }}</RouterLink>
      </div>
    </Section>
    <p v-if="error" role="alert" class="dashboard__error">{{ error }}</p>
    <div v-if="loading || report" class="dashboard__status-row">
      <p v-if="report" class="dashboard__note">{{ t('visualGame.dashboard.updated', { time: new Date(report.generated_at).toLocaleString(locale, { timeZone: 'UTC' }) }) }} · {{ report.since.slice(0, 10) }} — {{ new Date(Date.parse(report.until) - 1).toISOString().slice(0, 10) }}</p>
      <div v-if="loading" class="dashboard__loading-status" role="status" aria-live="polite" data-testid="dashboard-loading-status">
        <Spinner size="sm" aria-hidden="true" />
        <span>{{ t(`visualGame.dashboard.loading.${report ? 'refreshing' : 'initial'}`) }}</span>
      </div>
    </div>
    <div v-if="loading && !report" class="dashboard__report" aria-busy="true" data-testid="dashboard-skeleton">
      <div class="dashboard__summaries" aria-hidden="true">
        <Section v-for="scope in ['new', 'cumulative']" :key="scope" class="dashboard__summary" :title="t(`visualGame.dashboard.summary.${scope}`)">
          <div class="dashboard__skeleton-line dashboard__skeleton-note" />
          <div class="dashboard__cards">
            <div v-for="item in summary_metrics.length + 1" :key="item" class="dashboard__tile dashboard__skeleton-tile">
              <div class="dashboard__skeleton-line dashboard__skeleton-value" />
              <div class="dashboard__skeleton-line dashboard__skeleton-label" />
            </div>
          </div>
        </Section>
      </div>
      <Section aria-hidden="true">
        <div class="dashboard__skeleton-line dashboard__skeleton-note" />
        <div class="dashboard__skeleton-line" />
      </Section>
      <div class="dashboard__charts" aria-hidden="true">
        <Section v-for="title in ['growth.title', 'growth.cumulativeTitle', ...groups.map(group => `groups.${group.key}`), 'revenue']" :key="title" :title="t(`visualGame.dashboard.${title}`)">
          <div class="dashboard__skeleton-line" />
          <div class="dashboard__skeleton-chart" />
        </Section>
      </div>
    </div>
    <div v-if="report" class="dashboard__report" :aria-busy="loading" data-testid="dashboard-report">

      <div class="dashboard__summaries">
        <Section v-for="summary in summaries" :key="summary.scope" :class="['dashboard__summary', `dashboard__summary--${summary.scope}`]" :title="t(`visualGame.dashboard.summary.${summary.scope}`)">
          <p class="dashboard__summary-note">{{ t(`visualGame.dashboard.summary.${summary.scope}Note`) }}</p>
          <dl class="dashboard__cards">
            <div v-for="item in summary.rows" :key="item.key" class="dashboard__tile">
              <dt>{{ item.label }}</dt>
              <dd>{{ item.value }}</dd>
            </div>
          </dl>
        </Section>
      </div>
      <Section>
        <label for="dashboard-mode">{{ t('visualGame.dashboard.measure') }}</label>
        <select id="dashboard-mode" v-model="mode"><option value="count">{{ t('visualGame.dashboard.count') }}</option><option value="users">{{ t('visualGame.dashboard.users') }}</option></select>
        <p class="dashboard__note">{{ t('visualGame.dashboard.methodology') }}</p>
      </Section>
      <div class="dashboard__charts">
        <Section :title="t('visualGame.dashboard.growth.title')" data-testid="dashboard-user-growth">
          <p class="dashboard__note">{{ t('visualGame.dashboard.growth.note') }}</p>
          <DashboardTrend :dates="dates" :series="growth_series" />
          <p class="dashboard__growth-summary">{{ t('visualGame.dashboard.growth.summary', { added: number(report.metrics.registrations.total.count), active: number(report.metrics.active_users.total.users) }) }}</p>
        </Section>
        <Section :title="t('visualGame.dashboard.growth.cumulativeTitle')" data-testid="dashboard-cumulative-users">
          <p class="dashboard__note">{{ t('visualGame.dashboard.growth.cumulativeNote') }}</p>
          <DashboardTrend :dates="dates" :series="cumulative_user_series" />
          <p class="dashboard__growth-summary">{{ t('visualGame.dashboard.growth.cumulativeSummary', { count: number(report.cumulative.registrations.count) }) }}</p>
        </Section>
        <Section v-for="group in charts" :key="group.key" :title="t(`visualGame.dashboard.groups.${group.key}`)">
          <p class="dashboard__note">{{ t(`visualGame.dashboard.notes.${group.key}`) }}</p>
          <p v-if="group.series.every(item => item.values.every(value => value === 0))" class="dashboard__note">{{ t('visualGame.dashboard.empty') }}</p>
          <DashboardTrend :dates="dates" :series="group.series" />
        </Section>
        <Section :title="t('visualGame.dashboard.revenue')">
          <p class="dashboard__note">{{ t('visualGame.dashboard.revenueNote') }}</p>
          <DashboardTrend v-if="revenue_series.length" :dates="dates" :series="revenue_series" />
          <p v-else class="dashboard__note">{{ t('visualGame.dashboard.empty') }}</p>
        </Section>

        <template v-if="can_view_models">
          <Section v-for="kind in ['calls', 'tokens']" :key="kind" :title="t(`visualGame.dashboard.models.${kind === 'calls' ? 'callTrend' : 'tokenTrend'}`)" :data-testid="`dashboard-model-${kind}`" :aria-busy="model_loading">
            <p class="dashboard__note">{{ t(`visualGame.dashboard.models.${kind === 'calls' ? 'callNote' : 'tokenNote'}`) }}</p>
            <div v-if="model_loading" class="dashboard__skeleton-chart" :aria-label="t('visualGame.dashboard.loading.initial')" role="status" />
            <div v-else-if="model_error" role="alert">
              <p class="dashboard__error">{{ model_error }}</p>
              <Button variant="secondary" size="sm" @click="loadModelUsage({ since: report.since, until: report.until, device_types: report_device })">{{ t('visualGameAdmin.analytics.refresh') }}</Button>
            </div>
            <template v-else-if="model_usage">
              <DashboardTrend :dates="dates" :series="model_series[kind]" />
              <p class="dashboard__growth-summary">{{ kind === 'calls'
                ? t('visualGame.dashboard.models.callSummary', { count: number(model_usage.total.calls) })
                : t('visualGame.dashboard.models.tokenSummary', { count: model_usage.total.total_tokens === null ? t('visualGame.dashboard.models.unknown') : number(model_usage.total.total_tokens) }) }}</p>
              <p v-if="kind === 'tokens' && model_usage.total.missing_usage_calls" class="dashboard__note">{{ t('visualGame.dashboard.models.missing', { count: number(model_usage.total.missing_usage_calls) }) }}</p>
            </template>
          </Section>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dashboard__report { display: grid; gap: var(--drama-space-xl); }
.dashboard__status-row { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: var(--drama-space-sm); min-height: var(--drama-space-xl); }
.dashboard__loading-status { display: inline-flex; align-items: center; gap: var(--drama-space-sm); color: var(--drama-color-primary); font-size: var(--drama-font-size-sm); }
.dashboard__skeleton-line, .dashboard__skeleton-chart { background: var(--drama-color-border); border-radius: var(--drama-radius-sm); animation: dashboard-pulse 1.6s ease-in-out infinite; }
.dashboard__skeleton-line { height: var(--drama-space-md); width: 90%; }
.dashboard__skeleton-note { width: 55%; margin: var(--drama-space-sm) auto var(--drama-space-lg); }
.dashboard__skeleton-tile { min-height: 94px; gap: var(--drama-space-md); }
.dashboard__skeleton-value { width: 65%; height: var(--drama-font-size-xl); }
.dashboard__skeleton-label { width: 80%; height: var(--drama-font-size-xs); }
.dashboard__skeleton-chart { height: 320px; margin-top: var(--drama-space-lg); background: var(--drama-color-surface-alt); border-bottom: 1px solid var(--drama-color-border); }
@keyframes dashboard-pulse { 0%, 100% { opacity: 0.45; } 50% { opacity: 0.9; } }
@media (prefers-reduced-motion: reduce) { .dashboard__skeleton-line, .dashboard__skeleton-chart { animation: none; } }

.dashboard__summaries { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--drama-space-lg); }
.dashboard__summary :deep(.drama-section__header) { justify-content: center; }
.dashboard__summary-note { margin: 0 0 var(--drama-space-lg); text-align: center; color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.dashboard__cards { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: var(--drama-space-md); margin: 0; }
.dashboard__tile { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: var(--drama-space-xs); padding: var(--drama-space-lg) var(--drama-space-sm); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); background: var(--drama-color-surface-alt); text-align: center; min-width: 0; }
.dashboard__tile dd { order: -1; margin: 0; font-size: var(--drama-font-size-xl); font-weight: var(--drama-font-weight-bold); font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
.dashboard__tile dt { font-size: var(--drama-font-size-xs); color: var(--drama-color-text-muted); }
.dashboard__summary--new dd { color: var(--drama-color-success); }
.dashboard__summary--cumulative dd { color: var(--drama-color-info); }
.dashboard__charts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--drama-space-lg); }
.dashboard__presets { display: flex; align-items: center; flex-wrap: wrap; gap: var(--drama-space-sm); margin-top: var(--drama-space-md); }
.dashboard__preset:hover:not(:disabled) { border-color: var(--drama-color-primary); }
.dashboard__preset:focus-visible { outline: 2px solid var(--drama-color-primary); outline-offset: 2px; }
.dashboard__note { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); margin: var(--drama-space-sm) 0; }
.dashboard__growth-summary { color: var(--drama-color-success); font-size: var(--drama-font-size-sm); margin: var(--drama-space-md) 0 0; }
.dashboard__error { color: var(--drama-color-danger); }
.dashboard select { color: var(--drama-color-text); background: var(--drama-color-surface); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-sm); padding: var(--drama-space-sm); }
.dashboard a { color: var(--drama-color-primary); }
.dashboard select:focus-visible, .dashboard a:focus-visible { outline: 2px solid var(--drama-color-primary); outline-offset: 2px; }
@media (max-width: 1100px) { .dashboard__summaries { grid-template-columns: minmax(0, 1fr); } .dashboard__charts { grid-template-columns: minmax(0, 1fr); } }
@media (max-width: 600px) { .dashboard__cards { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
</style>
